"use client";

import useSWR from "swr";
import { Badge } from "@workspace/ui/components/badge";
import { GitCommit } from "lucide-react";

import { apiClient } from "@/lib/api-client";

/**
 * Commits that delivered this doc (tdd-0068).
 *
 * Data comes from the activity index via `/api/documents/:docId/commits`,
 * which resolves the platform doc id to the frontmatter id server-side.
 *
 * Each row shows *why* it matched: `subject` means the commit message named
 * this doc, `path` means the commit edited the doc's file. Phase markers
 * (`P1-6`) are surfaced when present, since they map a commit to a specific
 * phase of a TDD rather than the doc as a whole.
 */

/**
 * Gitea base for commit deep links (tdd-0068 P5). Links always render — Gitea is
 * JWT-gated at the gateway and applies its own restricted-user model, so it
 * enforces access itself rather than us mirroring that decision here.
 */
const GITEA_COMMIT_BASE =
  process.env.NEXT_PUBLIC_GITEA_URL || "https://git.l.gaz.codes";
const GIT_REPO_PATH =
  process.env.NEXT_PUBLIC_GIT_REPO_PATH || "gazjessep/gaz.codes";

interface CommitHit {
  commit: {
    sha: string;
    shortSha: string;
    date: string;
    subject: string;
    type: string | null;
    scope: string | null;
  };
  via: "subject" | "path";
  confidence: string;
  phase?: string;
  fileCount: number;
  insertions: number;
  deletions: number;
}

interface CommitsResponse {
  commits: CommitHit[];
  docId: string | null;
  warming?: boolean;
  error?: string;
}

interface CommitsProps {
  documentId: string;
}

const fetcher = (url: string): Promise<CommitsResponse> =>
  apiClient.get<CommitsResponse>(url).then((r) => r.data);

/**
 * Relative time with a long tail — commits span ~9 months, so this needs
 * minute granularity for today's work and year granularity for the oldest.
 * Neither existing helper covers both (`timeAgo` stops at days,
 * `formatRelativeDate` starts at days).
 */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export function Commits({ documentId }: CommitsProps) {
  const { data, error, isLoading, mutate } = useSWR<CommitsResponse>(
    `/api/documents/${documentId}/commits`,
    fetcher,
  );

  if (isLoading) {
    return <p className="p-3 text-sm text-muted-foreground">Loading commits…</p>;
  }

  // The index is optional infrastructure — a doc must still render without it.
  if (error) {
    return (
      <div className="p-3 space-y-2">
        <p className="text-sm text-muted-foreground">
          Activity index unavailable.
        </p>
        <button
          onClick={() => void mutate()}
          className="text-sm underline text-muted-foreground hover:text-foreground"
        >
          Retry
        </button>
      </div>
    );
  }

  if (data?.warming) {
    return (
      <div className="p-3 space-y-2">
        <p className="text-sm text-muted-foreground">
          Building the activity index…
        </p>
        <button
          onClick={() => void mutate()}
          className="text-sm underline text-muted-foreground hover:text-foreground"
        >
          Refresh
        </button>
      </div>
    );
  }

  if (!data?.docId) {
    return (
      <p className="p-3 text-sm text-muted-foreground">
        Not an engineering doc — no commit history.
      </p>
    );
  }

  const commits = data.commits ?? [];

  if (commits.length === 0) {
    return (
      <p className="p-3 text-sm text-muted-foreground">
        No commits reference <span className="font-mono">{data.docId}</span> yet.
      </p>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="px-3 py-2 text-sm text-muted-foreground border-b">
        {commits.length} commit{commits.length === 1 ? "" : "s"} referencing{" "}
        <span className="font-mono">{data.docId}</span>
      </div>
      <ul className="divide-y">
        {commits.map((hit) => (
          <li key={`${hit.commit.sha}-${hit.via}`} className="px-3 py-2.5 space-y-1.5">
            <div className="flex items-start gap-2">
              <GitCommit className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
              <p className="text-sm leading-snug break-words">{hit.commit.subject}</p>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 pl-6">
              {/* Deep link to the commit in Gitea. Uses the full sha — short
                  ones can collide as history grows. */}
              <a
                href={`${GITEA_COMMIT_BASE}/${GIT_REPO_PATH}/commit/${hit.commit.sha}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
                title="View this commit in Gitea"
              >
                {hit.commit.shortSha}
              </a>
              <span className="text-xs text-muted-foreground">
                {relativeTime(hit.commit.date)}
              </span>
              {hit.phase ? (
                <Badge variant="default" className="text-xs">
                  {hit.phase}
                </Badge>
              ) : null}
              {/* Provenance: did the message name this doc, or did the commit
                  edit its file? Both are reliable, but they mean different
                  things — "delivered by" vs "edited". */}
              <Badge variant="outline" className="text-xs">
                {hit.via === "subject" ? "referenced" : "edited"}
              </Badge>
            </div>

            <div className="pl-6 text-xs text-muted-foreground">
              {hit.fileCount} file{hit.fileCount === 1 ? "" : "s"}
              {hit.insertions > 0 || hit.deletions > 0 ? (
                <>
                  {" · "}
                  <span className="text-emerald-600 dark:text-emerald-500">
                    +{hit.insertions}
                  </span>{" "}
                  <span className="text-red-600 dark:text-red-500">
                    −{hit.deletions}
                  </span>
                </>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
